import { useQuery } from '@powersync/react';
import { useEffect, useMemo, useState } from 'react';

import { captureItem, sortKeyAfter } from '@flow/domain';
import {
  applyLocalOperations,
  queries,
  type CaptureDestinationRow,
  type CaptureTagRow,
  type CaptureTaxonomyRow,
} from '@flow/sync';
import { CaptureSheet } from '@flow/ui/components';

import type { LocalDatabase } from '../db/powersync.js';
import { t } from '../i18n/es.js';

import { buildTagIndex, interpret, todayIn, type TagIndex } from './model.js';

/**
 * Quick capture wired to the local database (CAP-01..04): parses as the user types, resolves the
 * tags against the workspace's, and saves through `captureItem` in one local transaction, never
 * waiting for the network (docs/07 §4.2).
 */

export const KEEP_OPEN_KEY = 'flow.capture.keepOpen';

function readKeepOpen(): boolean {
  try {
    return window.localStorage.getItem(KEEP_OPEN_KEY) === '1';
  } catch {
    return false;
  }
}

function writeKeepOpen(value: boolean) {
  try {
    window.localStorage.setItem(KEEP_OPEN_KEY, value ? '1' : '0');
  } catch {
    // Private windows can refuse storage: the choice lasts for this session only.
  }
}

export async function readTagIndex(
  db: LocalDatabase['db'],
  workspaceId: string,
): Promise<TagIndex> {
  const taxonomiesQuery = queries.captureTaxonomies(workspaceId);
  const tagsQuery = queries.captureTags(workspaceId);
  const [taxonomies, tags] = await Promise.all([
    db.getAll<CaptureTaxonomyRow>(taxonomiesQuery.sql, taxonomiesQuery.params),
    db.getAll<CaptureTagRow>(tagsQuery.sql, tagsQuery.params),
  ]);
  return buildTagIndex(taxonomies, tags);
}

const EMPTY_INDEX: TagIndex = { taxonomies: new Map(), tags: new Map() };

export interface QuickCaptureProps {
  open: boolean;
  onClose: () => void;
  local: LocalDatabase;
  userId: string;
  workspaceId: string;
}

export function QuickCapture({ open, onClose, local, userId, workspaceId }: QuickCaptureProps) {
  const [text, setText] = useState('');
  const [dismissed, setDismissed] = useState<string[]>([]);
  const [chosenDestination, setChosenDestination] = useState<string | null>(null);
  const [keepOpen, setKeepOpen] = useState(readKeepOpen);
  const [error, setError] = useState<string | null>(null);
  const [index, setIndex] = useState<TagIndex>(EMPTY_INDEX);

  const destinationsQuery = queries.captureDestinations(workspaceId);
  const { data: destinations } = useQuery<CaptureDestinationRow>(
    destinationsQuery.sql,
    destinationsQuery.params,
  );
  const preferencesQuery = queries.userPreferences(userId);
  const { data: preferences } = useQuery<{ timezone: string | null }>(
    preferencesQuery.sql,
    preferencesQuery.params,
  );
  const timeZone = preferences[0]?.timezone ?? null;

  // The workspace's tags, read once per opening (and after a save with new ones): not watched.
  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    void readTagIndex(local.db, workspaceId).then((next) => {
      if (!cancelled) setIndex(next);
    });
    return () => {
      cancelled = true;
    };
  }, [open, local.db, workspaceId]);

  const captureStage = destinations.find((stage) => stage.is_capture_default === 1);
  const destination =
    destinations.find((stage) => stage.id === chosenDestination) ?? captureStage ?? destinations[0];

  const today = todayIn(timeZone);
  const interpretation = useMemo(
    () => interpret(text, { today, dismissed, index }),
    [text, today, dismissed, index],
  );

  const reset = () => {
    setText('');
    setDismissed([]);
    setError(null);
  };

  const close = () => {
    reset();
    setChosenDestination(null);
    onClose();
  };

  const save = async (): Promise<boolean> => {
    if (!destination || text.trim() === '') return false;
    let created = false;
    try {
      // Saved before the tags were read (a very fast first capture): read them now, so no prefix
      // is taken for plain text.
      const current = index === EMPTY_INDEX ? await readTagIndex(local.db, workspaceId) : index;
      const { parse, tags } =
        current === index ? interpretation : interpret(text, { today, dismissed, index: current });
      created = tags.some((tag) => tag.isNew);
      // One bounded read for the sort key (by_workspace_sort, LIMIT 1): exact after the previous
      // save, since writes are awaited.
      const lastQuery = queries.lastSortKey(workspaceId);
      const last = await local.db.getOptional<{ last: string | null }>(
        lastQuery.sql,
        lastQuery.params,
      );
      const operations = captureItem({
        workspace_id: workspaceId,
        created_by: userId,
        title: parse.title,
        due_on: parse.due_on,
        start_on: parse.start_on,
        sort_key: sortKeyAfter(last?.last ?? null),
        stage: {
          id: destination.id,
          workspace_id: destination.workspace_id,
          category: destination.category,
          kind_scope: destination.kind_scope,
        },
        tags: tags.map((tag) => tag.input),
      });
      await applyLocalOperations(local.db, operations);
    } catch (cause) {
      console.error('[flow] captura no guardada', cause);
      setError(t.quickCapture.error);
      return false;
    }
    if (keepOpen) {
      reset();
      // New tags now exist: the next capture shows them as existing ones.
      if (created) void readTagIndex(local.db, workspaceId).then(setIndex);
    } else {
      close();
    }
    return true;
  };

  return (
    <CaptureSheet
      open={open}
      text={text}
      onTextChange={(value) => {
        setText(value);
        setError(null);
      }}
      chips={interpretation.chips}
      onDismissChip={(key) => setDismissed((keys) => [...keys, key])}
      destinations={destinations.map((stage) => ({ id: stage.id, name: stage.name }))}
      destinationId={destination?.id ?? ''}
      onDestinationChange={setChosenDestination}
      keepOpen={keepOpen}
      onKeepOpenChange={(value) => {
        setKeepOpen(value);
        writeKeepOpen(value);
      }}
      onSave={save}
      onClose={close}
      error={error}
      labels={{
        title: t.quickCapture.title,
        cancel: t.quickCapture.cancel,
        save: t.quickCapture.save,
        field: t.quickCapture.field,
        placeholder: t.quickCapture.placeholder,
        detected: t.quickCapture.detected,
        remove: (chip) => t.quickCapture.remove(chip.label, chip.value),
        newTag: t.quickCapture.newTag,
        saveIn: t.quickCapture.saveIn,
        keepOpen: t.quickCapture.keepOpen,
        hint: t.quickCapture.hint,
        announce: (chips) =>
          t.quickCapture.announce(chips.map((chip) => `${chip.label} ${chip.value}`)),
      }}
    />
  );
}
